import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";

const API = API_BASE_URL;

export const MILESTONE_BADGES = [
  {
    key: "first_lesson",
    label: "First Lesson",
    description: "Complete your first Writeflow lesson.",
    icon: "1",
  },
  {
    key: "week_1_complete",
    label: "Week 1 Complete",
    description: "Complete all lessons in Week 1.",
    icon: "W1",
  },
  {
    key: "halfway_there",
    label: "Halfway There",
    description: "Complete Week 2.",
    icon: "50",
  },
  {
    key: "final_week",
    label: "Final Week",
    description: "Reach Week 4.",
    icon: "4",
  },
  {
    key: "revision_writer",
    label: "Revision Writer",
    description: "Pass Week 4, Lesson 1.",
    icon: "↺",
  },
  {
    key: "course_complete",
    label: "Course Complete",
    description: "Complete the full course.",
    icon: "✓",
  },
];

function collectCompletedLessonIds(trendLessons) {
  const completed = new Set();
  for (const lesson of Array.isArray(trendLessons) ? trendLessons : []) {
    const lessonId = String(lesson?.lesson_id || "").trim().toUpperCase();
    if (!lessonId) continue;
    if (lesson?.final?.overall == null && lesson?.overall == null) continue;
    completed.add(lessonId);
  }
  return completed;
}

export function buildMilestoneBadges({
  currentWeek = 1,
  trendLessons = [],
} = {}) {
  const completedLessons = collectCompletedLessonIds(trendLessons);

  const milestoneMap = {
    first_lesson: completedLessons.has("W1L1"),
    week_1_complete: completedLessons.has("W1L4"),
    halfway_there: completedLessons.has("W2L4"),
    final_week: currentWeek >= 4,
    revision_writer: completedLessons.has("W4L1"),
    course_complete: completedLessons.has("W4L4"),
  };

  return MILESTONE_BADGES.map((badge) => ({
    ...badge,
    earned: Boolean(milestoneMap[badge.key]),
  }));
}

export function countEarnedMilestonesFromTrendLessons(trendLessons, progress = {}) {
  const badges = buildMilestoneBadges({
    currentWeek: progress.currentWeek,
    currentLesson: progress.currentLesson,
    trendLessons,
  });
  return {
    earned: badges.filter((badge) => badge.earned).length,
    total: badges.length,
  };
}

export default function StudentBadges({ outletContext } = {}) {
  const routeOutlet = useOutletContext() || {};
  const { studentId } = outletContext || routeOutlet;
  const currentWeekFromOutlet = Number((outletContext || routeOutlet)?.currentWeek || 1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [trendLessons, setTrendLessons] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(1);
  const didLoadRef = useRef(false);

  useEffect(() => {
    if (!studentId) return undefined;
    let cancelled = false;

    async function loadBadges() {
      if (!didLoadRef.current) setLoading(true);
      setError("");
      try {
        const dashboardRes = await fetch(`${API}/student/${encodeURIComponent(studentId)}/dashboard_v2`, {
          cache: "no-store",
        });
        const dashboardJson = await dashboardRes.json().catch(() => null);

        if (cancelled) return;

        if (!dashboardRes.ok) {
          setError("Badges are unavailable right now.");
          setTrendLessons([]);
        } else {
          setCurrentWeek(currentWeekFromOutlet || 1);
          setTrendLessons(Array.isArray(dashboardJson?.trend?.lessons) ? dashboardJson.trend.lessons : []);
          didLoadRef.current = true;
        }
      } catch {
        if (!cancelled) {
          setError("Badges are unavailable right now.");
          setTrendLessons([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadBadges();
    function onVisibility() {
      if (document.visibilityState === "visible") loadBadges();
    }
    document.addEventListener("visibilitychange", onVisibility);
    const onProgressRefetch = (event) => {
      const sid = String(event?.detail?.studentId || "").toUpperCase();
      if (sid && sid !== String(studentId || "").toUpperCase()) return;
      loadBadges();
    };
    window.addEventListener("studentProgressRefetch", onProgressRefetch);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("studentProgressRefetch", onProgressRefetch);
    };
  }, [studentId, currentWeekFromOutlet]);

  const milestoneBadges = useMemo(
    () =>
      buildMilestoneBadges({
        currentWeek,
        trendLessons,
      }),
    [currentWeek, trendLessons]
  );

  return (
    <section className="sp-content sp-badges-page">
      <div className="sp-badges-shell reveal" style={{ "--delay": ".05s" }}>
        <header className="sp-badges-header">
          <h1 className="sp-badges-title">Milestones</h1>
          <p className="sp-badges-subtitle">Track your journey and celebrate your achievements.</p>
        </header>

        {loading ? (
          <div className="sp-badges-status" role="status" aria-live="polite">
            Loading milestones…
          </div>
        ) : error ? (
          <div className="sp-badges-status" role="status" aria-live="polite">
            {error}
          </div>
        ) : (
          <div className="sp-milestone-grid" role="list" aria-label="Milestones">
            {milestoneBadges.map((badge) => (
              <div
                key={badge.key}
                className={`sp-milestone-card ${badge.earned ? "is-earned" : "is-locked"}`}
                role="listitem"
              >
                <div className="sp-milestone-icon" aria-hidden="true">
                  {badge.icon}
                </div>
                <div className="sp-milestone-title">{badge.label}</div>
                <div className="sp-milestone-desc">{badge.description}</div>
                <div className={`sp-milestone-pill ${badge.earned ? "is-earned" : "is-locked"}`}>
                  {badge.earned ? "Unlocked" : "Locked"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
